import {OrderByQuery, PagingQuery, parseEnum} from "orpyx-web-common";
import {GetPatientsListPropertyOrderBy, PatientFilterType, PatientFilterBy, PatientPriorityStatus} from "doctors-dashboard/http-clients/index";
import {PatientFilterTypeUrlPipe} from "../../../pipes/patient-filter-type-pipe";
import {parsePriorityStatus} from "../enum/patient-priority-status";

export class PatientsSearchQuery extends OrderByQuery<GetPatientsListPropertyOrderBy> {
    static readonly PARAM_FILTER_BY = "filterBy";
    static readonly PARAM_PRIORITY_STATUS = "priorityStatus";

    /// <summary>
    /// <para>Search users by</para>
    /// <para>- LastName or patient id</para>
    /// </summary>
    term?: string | undefined;

    patientsType?: PatientFilterType | undefined;

    filterBy?: PatientFilterBy | undefined;

    /// <summary>
    /// <para>Status pinned to the top of the list.</para>
    /// <para>Only used by the Inactive Patients list; omitted means no pinning.</para>
    /// <para>Sent as PriorityStatus alongside an OrderBy of Status, which is what the backend requires.</para>
    /// </summary>
    priorityStatus?: PatientPriorityStatus | undefined;

    /// <summary>
    /// <para>Default sort per tab, applied when the URL carries no explicit orderBy/orderByAsc. Each
    /// tab's default is only meaningful for a column that tab actually displays - e.g. Active is the
    /// only tab with a "Days Since Last Review" column, so it's the only one defaulting to
    /// LastReviewDate (see ActivePatientsListOrderPipeItems for why ascending means least-recent-first).
    /// InactivePatients defaults to LastStatusUpdatedAt ascending (least recently changed status
    /// first), matching the "Status Last Changed" column's own default sort intent.
    /// </para>
    /// </summary>
    private static readonly DEFAULT_ORDER_BY_PATIENT_TYPE: Record<PatientFilterType, {orderBy: GetPatientsListPropertyOrderBy, orderByAsc: boolean}> = {
        [PatientFilterType.Active]: {orderBy: GetPatientsListPropertyOrderBy.LastReviewDate, orderByAsc: true},
        [PatientFilterType.InactivePatients]: {orderBy: GetPatientsListPropertyOrderBy.LastStatusUpdatedAt, orderByAsc: true},
        [PatientFilterType.NewPatients]: {orderBy: GetPatientsListPropertyOrderBy.ClientPatientIdentifier, orderByAsc: true},
        [PatientFilterType.GlobalSearch]: {orderBy: GetPatientsListPropertyOrderBy.ClientPatientIdentifier, orderByAsc: true},
    };

    public static parse(term: string | undefined, filterBy: string | undefined,patientsType: string | undefined, priorityStatus: string | undefined, orderBy: string | undefined, orderByAsc: string | undefined, offset: string | undefined, count: string | undefined): PatientsSearchQuery {
        const patientsTypeEnum = parseEnum(PatientFilterType, patientsType, PatientFilterType.Active);
        const defaultOrderBy = PatientsSearchQuery.DEFAULT_ORDER_BY_PATIENT_TYPE[patientsTypeEnum ?? PatientFilterType.Active];
        const orderByValue = parseEnum(GetPatientsListPropertyOrderBy, orderBy, defaultOrderBy.orderBy);
        const filterByEnum = parseEnum(PatientFilterBy, filterBy, PatientFilterBy.Name);
        const priorityStatusValue = patientsTypeEnum === PatientFilterType.InactivePatients
            ? parsePriorityStatus(priorityStatus)
            : undefined;

        return new PatientsSearchQuery
        (
            term,
            filterByEnum,
            patientsTypeEnum,
            priorityStatusValue,

            orderByValue,
            orderByAsc !== undefined ? orderByAsc === "true" : defaultOrderBy.orderByAsc,

            offset ? parseInt(offset) : PagingQuery.DEFAULT_PAGE,
            count ? parseInt(count) : PagingQuery.DEFAULT_COUNT
        );
    }

    constructor(term?: string | undefined, filterBy?: PatientFilterBy, patientsType?: PatientFilterType, priorityStatus?: PatientPriorityStatus | undefined, orderBy?: GetPatientsListPropertyOrderBy, orderByAsc?: boolean, offset: number | undefined = PagingQuery.DEFAULT_PAGE, count: number | undefined = PagingQuery.DEFAULT_COUNT) {
        super(orderBy, orderByAsc, offset, count);
        this.patientsType = patientsType;
        this.filterBy = filterBy;
        this.priorityStatus = priorityStatus;
        this.term = term;
    }
}

export const toQueryString = (query: PatientsSearchQuery): string => {
    const patientsType = PatientFilterTypeUrlPipe(query.patientsType!);
    let str = `/patients/${patientsType}`;

    str += `?offset=${query.offset}&count=${query.count}`;

    if (query.term) {
        str += `&term=${query.term}`;
    }

    if (query.filterBy) {
        str += `&${PatientsSearchQuery.PARAM_FILTER_BY}=${query.filterBy}`;
    }

    if (query.priorityStatus) {
        str += `&${PatientsSearchQuery.PARAM_PRIORITY_STATUS}=${query.priorityStatus}`;
    }

    if (query.orderBy) {
        str += `&${PatientsSearchQuery.PARAM_ORDER_BY}=${query.orderBy}`;
    }

    // A truthy check would drop orderByAsc=false from the URL indistinguishably from "unset",
    // silently reverting descending sort selections back to whichever direction is the tab's default.
    if (query.orderByAsc !== undefined) {
        str += `&${PatientsSearchQuery.PARAM_ORDER_BY_ASC}=${query.orderByAsc}`;
    }

    return str;
};
